// Projections & simulations hub — the Phase B surface.
// Sub-tabs:
//   Lifetime  — Lifetime Earnings stacked-area chart of the baseline projection
//   Scenarios — Multi-scenario side-by-side compare
//   FIRE      — 5-variant FIRE dashboard
//   Home      — Home Ownership Planner (buy vs rent)
//   Career    — Career ROI calculator
//
// Every tab consumes the same shared <AssumptionsPanel/> and mounts a
// <ChartInsightsCard/> so no chart is ever unexplained.
//
// Per-tab entitlement gating is enforced BOTH on the client (tab is locked +
// shows upsell) and on the server (402 response). The upsell card is
// identical whether the lock is client-side or driven by a 402.
import { useEffect, useState } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend,
} from "recharts";
import { LineChart as LineIcon, Home, Briefcase, GitCompare, Flame, Lock, AlertTriangle, Dices } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useFinancialPlan } from "@/context/FinancialPlanContext";
import { useEntitlements } from "@/context/EntitlementsContext";
import { useCurrency } from "@/context/CurrencyContext";
import AssumptionsPanel from "./AssumptionsPanel";
import ChartInsightsCard from "./ChartInsightsCard";

// tabId → { label, icon, feature_key, tier }
const TABS = [
  { id: "lifetime",    label: "Lifetime earnings", icon: LineIcon,   feature: "lifetime_earnings_projection", tier: "Pro" },
  { id: "scenarios",   label: "Scenarios",         icon: GitCompare, feature: "financial_v2_scenarios_multi", tier: "Premium" },
  { id: "fire",        label: "FIRE",              icon: Flame,      feature: "financial_v2_fire",            tier: "Premium" },
  { id: "home",        label: "Home",              icon: Home,       feature: "financial_v2_home_planner",    tier: "Premium" },
  { id: "career",      label: "Career ROI",        icon: Briefcase,  feature: "career_roi_analysis",          tier: "Premium" },
  { id: "montecarlo",  label: "Monte Carlo",       icon: Dices,      feature: "financial_v2_monte_carlo",     tier: "Premium" },
];

const TAB_UPSELL = {
  lifetime:   "Lifetime earnings projections model your salary growth, taxes, and net-worth trajectory over 40+ years. Pro unlocks the full deterministic engine.",
  scenarios:  "Scenarios compares Baseline vs promotion, sabbatical, retirement age changes, and more — all side-by-side. Premium unlocks unlimited scenarios.",
  fire:       "The FIRE dashboard shows 5 variants (Lean, Coast, Barista, Traditional, Fat) and when you hit each. Premium unlocks the full breakdown.",
  home:       "Home Ownership Planner runs full mortgage amortization plus a Buy-vs-Rent-and-invest comparison. Premium unlocks the planner.",
  career:     "Career ROI models tuition, gap-year cost, break-even, and lifetime-income delta of switching careers. Premium unlocks the ROI simulator.",
  montecarlo: "Monte Carlo runs thousands of sampled trajectories to give you probability bands (P10 → P90) instead of a single \"if the market returns 7%\" guess. Premium unlocks it.",
};

export default function ProjectionsSection() {
  const { plan } = useFinancialPlan();
  const { currency: userCcy } = useCurrency();
  // Plan currency wins (backend guarantees non-USD-defaulted plans via
  // resolve_user_currency). Fall back to the user's resolved currency from
  // CurrencyContext — never a hardcoded "USD".
  const currency = plan?.currency || userCcy;
  const [tab, setTab] = useState("lifetime");
  const [assumptions, setAssumptions] = useState({
    years: 40, inflation_pct: 2.5, default_return_pct: 7.0, salary_growth_pct: 3.0,
  });
  const { has } = useEntitlements();

  const canView = (t) => has(t.feature);

  // Detect an incomplete plan — we need at least one income source to run a
  // meaningful projection. Everything else can default.
  const planIsIncomplete = !plan || !(plan.income_sources || []).some(
    (s) => Number(s.amount_annual || 0) > 0
  );

  return (
    <div className="space-y-4" data-testid="projections-section">
      <div className="flex flex-wrap gap-2" data-testid="projections-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          const locked = !canView(t);
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-medium transition-colors border ${
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border bg-card/50 hover:bg-card"
              }`}
              data-testid={`projections-tab-${t.id}`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
              {locked && <Lock className="w-3 h-3 ml-1 opacity-70" />}
            </button>
          );
        })}
      </div>

      <AssumptionsPanel value={assumptions} onChange={setAssumptions} />

      {/* Client-side gate: don't fire the API call for a tab the user cannot use. */}
      {(() => {
        const current = TABS.find((t) => t.id === tab);
        if (!canView(current)) {
          return (
            <LockedTab
              tabId={tab}
              tier={current.tier}
              featureKey={current.feature}
              pitch={TAB_UPSELL[tab]}
            />
          );
        }
        if (planIsIncomplete) {
          return <IncompletePlanNotice />;
        }
        if (tab === "lifetime")   return <LifetimeTab    currency={currency} assumptions={assumptions} />;
        if (tab === "scenarios")  return <ScenariosTab   currency={currency} assumptions={assumptions} />;
        if (tab === "fire")       return <FireTab        currency={currency} assumptions={assumptions} />;
        if (tab === "home")       return <HomeTab        currency={currency} assumptions={assumptions} />;
        if (tab === "career")     return <CareerTab      currency={currency} assumptions={assumptions} />;
        if (tab === "montecarlo") return <MonteCarloTab  currency={currency} assumptions={assumptions} />;
        return null;
      })()}
    </div>
  );
}

// ---------------- helpers ----------------
function useMoney(currency) {
  const { format } = useCurrency();
  return (v) => {
    if (v == null || Number.isNaN(v)) return "—";
    const a = Math.abs(v);
    // For huge charts we prefer compact notation. For KPI cells we prefer full.
    // Callers pick between fmtCompact (locally) and fmtFull.
    return format(v, { currency });
  };
}

function fmtCompact(v, ccy = "") {
  if (v == null || Number.isNaN(v)) return "—";
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  const num =
    a >= 1_000_000 ? `${(a / 1_000_000).toFixed(2)}M` :
    a >= 1_000     ? `${Math.round(a / 1_000)}K` :
    Math.round(a).toLocaleString();
  return ccy ? `${sign}${ccy} ${num}` : `${sign}${num}`;
}

const overridesFromAssumptions = (a) => ({
  inflation_pct: a.inflation_pct,
  default_return_pct: a.default_return_pct,
  salary_growth_pct: a.salary_growth_pct,
});

/**
 * useProject: fires POST to `endpoint` with `body`, tracks loading/error.
 * Handles 402 (entitlement) by returning `.entitlementError` so callers can
 * render an upsell instead of a generic error banner.
 */
function useProject(endpoint, body, deps) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [entitlementError, setEntErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setEntErr(null);
    api.post(endpoint, body)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 402 && detail && typeof detail === "object" && detail.error === "entitlement_required") {
          setEntErr(detail);
        } else if (typeof detail === "string") {
          setErr(detail);
        } else if (detail?.message) {
          setErr(detail.message);
        } else {
          setErr(e.message || "Something went wrong");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, deps);
  return { data, loading, err, entitlementError };
}

// ================ Lifetime Earnings ================
function LifetimeTab({ currency, assumptions }) {
  const body = {
    years: assumptions.years, events: [],
    overrides: overridesFromAssumptions(assumptions),
    scenario_name: "Baseline",
  };
  const { data, loading, err, entitlementError } = useProject("/financial-plan/project", body,
    [assumptions.years, assumptions.inflation_pct, assumptions.default_return_pct, assumptions.salary_growth_pct]);

  if (loading) return <SkeletonCard />;
  if (entitlementError) return <LockedTab tabId="lifetime" tier={entitlementError.upsell_tier || "Pro"} featureKey={entitlementError.feature_key} pitch={TAB_UPSELL.lifetime} />;
  if (err)     return <ErrCard msg={err} />;
  if (!data)   return null;

  const rows = data.years.map(r => ({
    year: r.year, age: r.age,
    Taxes: r.taxes,
    "Take-home": Math.max(0, r.take_home),
    "Net worth": r.net_worth,
    "Investments+Savings": r.investments_balance + r.savings_balance,
  }));

  return (
    <div className="space-y-4">
      <KpiRow kpis={data.kpis} currency={currency} />
      <div className="rounded-xl border border-border bg-card/40 p-4" data-testid="lifetime-chart">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-semibold">Lifetime earnings & net worth</div>
          <div className="text-[11px] text-muted-foreground font-mono">
            {data.kpis.start_age}yo → {data.kpis.end_age}yo
          </div>
        </div>
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="gTaxes" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.7} />
                <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.15} />
              </linearGradient>
              <linearGradient id="gTake" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.7} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.15} />
              </linearGradient>
              <linearGradient id="gNW" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.85} />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.2} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.5} vertical={false} />
            <XAxis dataKey="year" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => fmtCompact(v)} width={60} />
            <Tooltip
              formatter={(v) => fmtCompact(v, currency)}
              contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12, color: "hsl(var(--popover-foreground))" }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="Taxes"      stroke="#f59e0b" fill="url(#gTaxes)" stackId="1" />
            <Area type="monotone" dataKey="Take-home"  stroke="#3b82f6" fill="url(#gTake)"  stackId="1" />
            <Area type="monotone" dataKey="Net worth"  stroke="#8b5cf6" fill="url(#gNW)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <ChartInsightsCard insights={data.insights} />
    </div>
  );
}

function KpiRow({ kpis, currency }) {
  const money = useMoney(currency);
  const cells = [
    { label: "Lifetime earnings",  value: money(kpis.lifetime_earnings) },
    { label: "Lifetime taxes",     value: money(kpis.lifetime_taxes) },
    { label: "Final net worth",    value: money(kpis.final_net_worth) },
    { label: "FIRE age",           value: kpis.fire_age ?? "—" },
    { label: "Debt-free by",       value: kpis.debt_free_year ?? "—" },
    { label: "Effective tax rate", value: `${kpis.effective_tax_rate_pct ?? 0}%` },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="kpi-row">
      {cells.map((c) => (
        <div key={c.label} className="rounded-lg border border-border bg-card/40 px-3 py-2.5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{c.label}</div>
          <div className="mt-1 text-sm font-display font-semibold tabular-nums">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

// ================ Scenario compare ================
const DEFAULT_SCENARIOS = [
  { name: "Baseline", events: [] },
  { name: "+25% promotion in 3 years", events: [{ kind: "promotion", year: 3, payload: { pct: 25 } }] },
  { name: "Retire at 55", events: [{ kind: "retirement", year: 26 }] },
];

function ScenariosTab({ currency, assumptions }) {
  const [scenarios] = useState(DEFAULT_SCENARIOS);
  const body = { years: assumptions.years, scenarios };
  const { data, loading, err, entitlementError } = useProject("/financial-plan/scenarios/compare", body,
    [assumptions.years, JSON.stringify(scenarios)]);

  if (loading) return <SkeletonCard />;
  if (entitlementError) return <LockedTab tabId="scenarios" tier={entitlementError.upsell_tier || "Premium"} featureKey={entitlementError.feature_key} pitch={TAB_UPSELL.scenarios} />;
  if (err) return <ErrCard msg={err} />;
  if (!data?.scenarios?.length) return null;

  const chartData = data.scenarios[0].years.map((row, i) => {
    const point = { year: row.year };
    data.scenarios.forEach((s) => {
      point[s.scenario_name] = s.years[i]?.net_worth;
    });
    return point;
  });
  const colors = ["#3b82f6", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6"];

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card/40 p-4" data-testid="scenarios-chart">
        <div className="font-display font-semibold mb-3">Net worth across scenarios</div>
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.5} vertical={false} />
            <XAxis dataKey="year" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => fmtCompact(v)} width={60} />
            <Tooltip formatter={(v) => fmtCompact(v, currency)} contentStyle={{ background:"hsl(var(--popover))", border:"1px solid hsl(var(--border))", borderRadius:8, fontSize:12, color:"hsl(var(--popover-foreground))" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {data.scenarios.map((s, i) => (
              <Area
                key={s.scenario_name}
                type="monotone"
                dataKey={s.scenario_name}
                stroke={colors[i % colors.length]}
                fill={colors[i % colors.length] + "33"}
                fillOpacity={1}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-xl border border-border bg-card/40 p-4 overflow-x-auto" data-testid="scenarios-diff-table">
        <div className="font-display font-semibold mb-3">Side-by-side comparison</div>
        <table className="w-full text-xs min-w-[500px]">
          <thead>
            <tr className="text-left text-muted-foreground border-b border-border">
              <th className="py-2 pr-4 font-mono uppercase tracking-widest">Metric</th>
              {data.scenarios.map((s) => (
                <th key={s.scenario_name} className="py-2 pr-4 font-mono uppercase tracking-widest">{s.scenario_name}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.diff.map((row) => (
              <tr key={row.kpi}>
                <td className="py-2 pr-4 text-muted-foreground">{row.label}</td>
                {row.values.map((v, i) => (
                  <td key={`${row.kpi}-${i}`} className="py-2 pr-4 tabular-nums">
                    {row.kind === "money" ? fmtCompact(v.value, currency) :
                     row.kind === "pct" ? (v.value != null ? `${v.value}%` : "—") :
                     v.value != null ? v.value : "—"}
                    {v.delta_vs_baseline != null && i > 0 ? (
                      <span className={`ml-2 text-[10px] font-mono ${
                        v.delta_vs_baseline > 0 ? "text-emerald-500" : v.delta_vs_baseline < 0 ? "text-red-500" : "text-muted-foreground"
                      }`}>
                        {v.delta_vs_baseline > 0 ? "▲" : v.delta_vs_baseline < 0 ? "▼" : ""}{" "}
                        {row.kind === "money" ? fmtCompact(v.delta_vs_baseline) : row.kind === "pct" ? `${v.delta_vs_baseline?.toFixed?.(1)}%` : v.delta_vs_baseline}
                      </span>
                    ) : null}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ChartInsightsCard insights={data.scenarios[0].insights} />
    </div>
  );
}

// ================ FIRE ================
function FireTab({ currency, assumptions }) {
  const [pt, setPt] = useState(0);
  const [ra, setRa] = useState(60);
  const body = { years: assumptions.years, part_time_income_annual: pt, target_retirement_age: ra };
  const { data, loading, err, entitlementError } = useProject("/financial-plan/fire", body,
    [assumptions.years, assumptions.inflation_pct, assumptions.default_return_pct, pt, ra]);

  if (loading) return <SkeletonCard />;
  if (entitlementError) return <LockedTab tabId="fire" tier={entitlementError.upsell_tier || "Premium"} featureKey={entitlementError.feature_key} pitch={TAB_UPSELL.fire} />;
  if (err) return <ErrCard msg={err} />;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <NumInput label="Part-time income (annual)" value={pt} onChange={setPt} testId="fire-pt" />
        <NumInput label="Target retirement age" value={ra} onChange={setRa} step={1} testId="fire-ra" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fire-variants">
        {data.variants.map((v) => (
          <div key={v.id} className={`rounded-xl border p-4 relative overflow-hidden ${
            v.pct_of_target_today >= 100 ? "border-emerald-500/40 bg-emerald-500/5" : "border-border bg-card/40"
          }`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{v.label}</div>
            <div className="mt-2 font-display font-bold text-xl tabular-nums">{fmtCompact(v.target, currency)}</div>
            <div className="mt-1 text-[11px] text-muted-foreground">
              {v.hit_age != null ? <>Hit age <span className="text-foreground font-semibold">{v.hit_age}</span></> : "Not reached in horizon"}
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary to-purple-500 transition-all"
                style={{ width: `${Math.min(100, v.pct_of_target_today)}%` }}
              />
            </div>
            <div className="mt-1 text-[10px] text-muted-foreground">{v.pct_of_target_today}% today</div>
            <p className="mt-2 text-[11px] text-muted-foreground leading-relaxed">{v.explanation}</p>
          </div>
        ))}
      </div>

      <ChartInsightsCard insights={data.baseline?.insights} />
    </div>
  );
}

// ================ Home ================
function HomeTab({ currency, assumptions }) {
  const [inputs, setInputs] = useState({
    price: 500000, down_payment: 100000, mortgage_rate_pct: 6.0,
    amortization_years: 25, annual_property_tax: 5500,
    annual_home_insurance: 1200, monthly_condo_fees: 0,
    annual_maintenance_pct: 1.0, closing_costs_pct: 2.0,
    current_monthly_rent: 2500,
  });
  const set = (k, v) => setInputs((s) => ({ ...s, [k]: v }));

  const body = { ...inputs, years: assumptions.years };
  const { data, loading, err, entitlementError } = useProject("/financial-plan/home-plan", body,
    [JSON.stringify(inputs), assumptions.years]);

  if (loading) return <SkeletonCard />;
  if (entitlementError) return <LockedTab tabId="home" tier={entitlementError.upsell_tier || "Premium"} featureKey={entitlementError.feature_key} pitch={TAB_UPSELL.home} />;
  if (err) return <ErrCard msg={err} />;
  if (!data) return null;

  const chart = data.buy.years.map((rb, i) => ({
    year: rb.year,
    "Net worth (buy)":  rb.net_worth,
    "Net worth (rent + invest)": data.rent.years[i]?.net_worth,
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <NumInput label="Home price" value={inputs.price} onChange={(v)=>set("price",v)} testId="home-price" />
        <NumInput label="Down payment" value={inputs.down_payment} onChange={(v)=>set("down_payment",v)} testId="home-dp" />
        <NumInput label="Mortgage rate %" value={inputs.mortgage_rate_pct} onChange={(v)=>set("mortgage_rate_pct",v)} step={0.1} testId="home-rate" />
        <NumInput label="Amortization (yr)" value={inputs.amortization_years} onChange={(v)=>set("amortization_years",v)} step={1} testId="home-amort" />
        <NumInput label="Property tax / yr" value={inputs.annual_property_tax} onChange={(v)=>set("annual_property_tax",v)} testId="home-ptax" />
        <NumInput label="Insurance / yr" value={inputs.annual_home_insurance} onChange={(v)=>set("annual_home_insurance",v)} testId="home-ins" />
        <NumInput label="Condo fees / mo" value={inputs.monthly_condo_fees} onChange={(v)=>set("monthly_condo_fees",v)} testId="home-condo" />
        <NumInput label="Current rent / mo" value={inputs.current_monthly_rent} onChange={(v)=>set("current_monthly_rent",v)} testId="home-rent" />
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Metric label="Monthly P&I"     value={fmtCompact(data.monthly_pi, currency)} />
        <Metric label="Monthly all-in"  value={fmtCompact(data.monthly_all_in, currency)} />
        <Metric label="Closing costs"   value={fmtCompact(data.closing_costs, currency)} />
        <Metric label="DTI"             value={`${data.affordability.dti_pct ?? "—"}%`}
                accent={data.affordability.verdict} />
        <Metric label="Break-even year" value={data.break_even_year ?? "—"} />
        <Metric label="Net-worth Δ (end)" value={fmtCompact(data.net_worth_delta_at_end, currency)}
                accent={data.net_worth_delta_at_end >= 0 ? "comfortable" : "risky"} />
      </div>

      <div className="rounded-xl border border-border bg-card/40 p-4" data-testid="home-chart">
        <div className="font-display font-semibold mb-3">Buy vs Rent + invest</div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={chart}>
            <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.5} vertical={false} />
            <XAxis dataKey="year" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v)=>fmtCompact(v)} width={60} />
            <Tooltip formatter={(v)=>fmtCompact(v,currency)} contentStyle={{ background:"hsl(var(--popover))", border:"1px solid hsl(var(--border))", borderRadius:8, fontSize:12, color:"hsl(var(--popover-foreground))" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="Net worth (buy)" stroke="#8b5cf6" fill="#8b5cf633" />
            <Area type="monotone" dataKey="Net worth (rent + invest)" stroke="#10b981" fill="#10b98133" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <ChartInsightsCard insights={data.buy?.insights} />
    </div>
  );
}

// ================ Career ROI ================
function CareerTab({ currency, assumptions }) {
  const [inputs, setInputs] = useState({
    target_role_label: "Senior PM",
    target_salary_annual: 160000,
    transition_start_year: 0,
    transition_gap_months: 12,
    tuition_cost: 40000,
    certification_cost: 0,
    opportunity_cost_years: 1,
  });
  const set = (k, v) => setInputs((s) => ({ ...s, [k]: v }));
  const body = { ...inputs, years: assumptions.years };
  const { data, loading, err, entitlementError } = useProject("/financial-plan/career-roi", body,
    [JSON.stringify(inputs), assumptions.years]);

  if (loading) return <SkeletonCard />;
  if (entitlementError) return <LockedTab tabId="career" tier={entitlementError.upsell_tier || "Premium"} featureKey={entitlementError.feature_key} pitch={TAB_UPSELL.career} />;
  if (err) return <ErrCard msg={err} />;
  if (!data) return null;

  const chart = data.baseline.years.map((rb, i) => ({
    year: rb.year,
    "Stay": rb.gross_income,
    [inputs.target_role_label || "New career"]: data.new_career.years[i]?.gross_income,
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <TextInput label="Target role label" value={inputs.target_role_label} onChange={(v)=>set("target_role_label",v)} testId="career-label" />
        <NumInput label="Target salary" value={inputs.target_salary_annual} onChange={(v)=>set("target_salary_annual",v)} testId="career-sal" />
        <NumInput label="Start year (0=now)" value={inputs.transition_start_year} onChange={(v)=>set("transition_start_year",v)} step={1} testId="career-start" />
        <NumInput label="Gap months (unpaid)" value={inputs.transition_gap_months} onChange={(v)=>set("transition_gap_months",v)} step={1} testId="career-gap" />
        <NumInput label="Tuition cost" value={inputs.tuition_cost} onChange={(v)=>set("tuition_cost",v)} testId="career-tuition" />
        <NumInput label="Certification cost" value={inputs.certification_cost} onChange={(v)=>set("certification_cost",v)} testId="career-cert" />
        <NumInput label="Opportunity-cost years" value={inputs.opportunity_cost_years} onChange={(v)=>set("opportunity_cost_years",v)} step={1} testId="career-oc" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <Metric label="Transition cost"    value={fmtCompact(data.comparison.total_transition_cost, currency)} />
        <Metric label="Lifetime income Δ"  value={fmtCompact(data.comparison.lifetime_income_delta, currency)}
                accent={data.comparison.lifetime_income_delta >= 0 ? "comfortable" : "risky"} />
        <Metric label="Payback (years)"    value={data.comparison.payback_period_years ?? "—"} />
        <Metric label="Break-even year"    value={data.comparison.break_even_year ?? "—"} />
        <Metric label="ROI %"              value={data.comparison.roi_pct != null ? `${data.comparison.roi_pct}%` : "—"} />
        <Metric label="FIRE age Δ"         value={data.comparison.fire_age_delta ?? "—"}
                accent={data.comparison.fire_age_delta && data.comparison.fire_age_delta < 0 ? "comfortable" : undefined} />
        <Metric label="Net-worth Δ (end)"  value={fmtCompact(data.comparison.net_worth_delta, currency)}
                accent={data.comparison.net_worth_delta >= 0 ? "comfortable" : "risky"} />
        <Metric label="Taxes Δ"            value={fmtCompact(data.comparison.lifetime_taxes_delta, currency)} />
      </div>

      <div className="rounded-xl border border-border bg-card/40 p-4" data-testid="career-chart">
        <div className="font-display font-semibold mb-3">Gross income: stay vs switch</div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={chart}>
            <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.5} vertical={false} />
            <XAxis dataKey="year" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v)=>fmtCompact(v)} width={60} />
            <Tooltip formatter={(v)=>fmtCompact(v,currency)} contentStyle={{ background:"hsl(var(--popover))", border:"1px solid hsl(var(--border))", borderRadius:8, fontSize:12, color:"hsl(var(--popover-foreground))" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="Stay" stroke="#94a3b8" fill="#94a3b833" />
            <Area type="monotone" dataKey={inputs.target_role_label || "New career"} stroke="#8b5cf6" fill="#8b5cf633" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <ChartInsightsCard insights={data.new_career?.insights} />
    </div>
  );
}

// ================ Monte Carlo ================
function MonteCarloTab({ currency, assumptions }) {
  const [inputs, setInputs] = useState({
    n_paths: 1000,
    seed: 42,
    stdev_return_pct: 15.0,
    stdev_inflation_pct: 1.5,
    retirement_age: 65,
    life_expectancy: 90,
    withdrawal_rate_pct: 4.0,
    target_net_worth: 1_500_000,
    target_year: 25,
  });
  const set = (k, v) => setInputs((s) => ({ ...s, [k]: v }));

  const body = {
    n_paths: inputs.n_paths,
    years: assumptions.years,
    seed: inputs.seed,
    mean_return_pct: assumptions.default_return_pct,
    stdev_return_pct: inputs.stdev_return_pct,
    mean_inflation_pct: assumptions.inflation_pct,
    stdev_inflation_pct: inputs.stdev_inflation_pct,
    mean_salary_growth_pct: assumptions.salary_growth_pct,
    retirement_age: inputs.retirement_age,
    life_expectancy: inputs.life_expectancy,
    withdrawal_rate_pct: inputs.withdrawal_rate_pct,
    target_net_worth: inputs.target_net_worth,
    target_year: inputs.target_year,
  };
  const { data, loading, err, entitlementError } = useProject("/financial-plan/monte-carlo", body,
    [JSON.stringify(inputs), assumptions.years, assumptions.inflation_pct, assumptions.default_return_pct, assumptions.salary_growth_pct]);

  if (loading) return <SkeletonCard />;
  if (entitlementError) return <LockedTab tabId="montecarlo" tier={entitlementError.upsell_tier || "Premium"} featureKey={entitlementError.feature_key} pitch={TAB_UPSELL.montecarlo} />;
  if (err) return <ErrCard msg={err} />;
  if (!data) return null;

  // Build chart data from percentile arrays
  const p = data.net_worth_percentiles;
  const chart = (p["50"] || []).map((_, i) => ({
    year: i + 1,
    p10: p["10"][i],
    p25: p["25"][i],
    p50: p["50"][i],
    p75: p["75"][i],
    p90: p["90"][i],
  }));

  const probTarget = data.probability_reach_target;
  const probShortfall = data.probability_retirement_shortfall;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <NumInput label="Simulated paths" value={inputs.n_paths} onChange={(v) => set("n_paths", v)} step={100} testId="mc-npaths" />
        <NumInput label="Seed (reproducibility)" value={inputs.seed} onChange={(v) => set("seed", v)} step={1} testId="mc-seed" />
        <NumInput label="Return volatility (σ %)" value={inputs.stdev_return_pct} onChange={(v) => set("stdev_return_pct", v)} step={0.5} testId="mc-stdev-return" />
        <NumInput label="Inflation volatility (σ %)" value={inputs.stdev_inflation_pct} onChange={(v) => set("stdev_inflation_pct", v)} step={0.1} testId="mc-stdev-inflation" />
        <NumInput label="Retirement age" value={inputs.retirement_age} onChange={(v) => set("retirement_age", v)} step={1} testId="mc-retire-age" />
        <NumInput label="Life expectancy" value={inputs.life_expectancy} onChange={(v) => set("life_expectancy", v)} step={1} testId="mc-life" />
        <NumInput label="Target net worth" value={inputs.target_net_worth} onChange={(v) => set("target_net_worth", v)} step={50000} testId="mc-target-nw" />
        <NumInput label="Target year" value={inputs.target_year} onChange={(v) => set("target_year", v)} step={1} testId="mc-target-year" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="mc-summary">
        <Metric label="P(reach target)"
                value={probTarget != null ? `${(probTarget * 100).toFixed(0)}%` : "—"}
                accent={probTarget != null && probTarget >= 0.75 ? "comfortable" : probTarget != null && probTarget < 0.4 ? "risky" : undefined} />
        <Metric label="P(retirement shortfall)"
                value={probShortfall != null ? `${(probShortfall * 100).toFixed(0)}%` : "—"}
                accent={probShortfall != null && probShortfall <= 0.15 ? "comfortable" : probShortfall != null && probShortfall > 0.5 ? "risky" : undefined} />
        <Metric label="Median final NW"     value={fmtCompact(data.final_net_worth.median, currency)} />
        <Metric label="P10 – P90 final NW"  value={`${fmtCompact(data.final_net_worth.p10)} – ${fmtCompact(data.final_net_worth.p90, currency)}`} />
      </div>

      {/* Property portfolio chip (Phase 4). Only rendered when the plan
          has active properties — otherwise nothing to say. */}
      {data.property_summary && data.property_summary.count > 0 && (
        <div
          className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 flex items-start gap-3"
          data-testid="mc-property-chip"
        >
          <div className="mt-0.5 text-primary shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M3 12l9-9 9 9M5 10v10a1 1 0 001 1h4v-6h4v6h4a1 1 0 001-1V10" />
            </svg>
          </div>
          <div className="flex-1 text-xs leading-relaxed">
            <div className="font-medium text-foreground mb-0.5">
              Property portfolio included in this simulation
            </div>
            <div className="text-muted-foreground">
              {data.property_summary.count} {data.property_summary.count === 1 ? "property" : "properties"} ·{" "}
              value {fmtCompact(data.property_summary.total_value, currency)} ·{" "}
              unlinked debt {fmtCompact(data.property_summary.total_debt, currency)}
              {data.property_summary.annual_rental_net_cash_flow !== 0 && (
                <> · net rental {fmtCompact(data.property_summary.annual_rental_net_cash_flow, currency)}/yr</>
              )}
              {" · "}appreciation ~{data.property_summary.weighted_appreciation_pct?.toFixed(1)}%/yr
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card/40 p-4" data-testid="mc-chart">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-semibold">Net worth — probability bands</div>
          <div className="text-[10px] text-muted-foreground font-mono">{data.n_paths} paths · seed {data.seed}</div>
        </div>
        <ResponsiveContainer width="100%" height={340}>
          <AreaChart data={chart} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="mcP90" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="mcP75" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.55} />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.15} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.5} vertical={false} />
            <XAxis dataKey="year" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => fmtCompact(v)} width={60} />
            <Tooltip
              formatter={(v) => fmtCompact(v, currency)}
              contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12, color: "hsl(var(--popover-foreground))" }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" name="P10–P90" dataKey="p90" stroke="none" fill="url(#mcP90)" stackId="a" />
            <Area type="monotone" dataKey="p10" stroke="none" fill="hsl(var(--background))" stackId="a" hide={false} legendType="none" />
            <Area type="monotone" name="P25–P75" dataKey="p75" stroke="none" fill="url(#mcP75)" />
            <Area type="monotone" name="Median" dataKey="p50" stroke="#8b5cf6" strokeWidth={2} fill="none" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 text-xs text-muted-foreground leading-relaxed flex gap-3" data-testid="mc-disclaimer">
        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
        <span>{data.disclaimer}</span>
      </div>
    </div>
  );
}

// ---------- shared primitives ----------
function Metric({ label, value, accent }) {
  const tint =
    accent === "comfortable" ? "text-emerald-500" :
    accent === "stretched" ? "text-amber-500" :
    accent === "risky" ? "text-red-500" : "";
  return (
    <div className="rounded-lg border border-border bg-card/40 px-3 py-2.5">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-1 text-sm font-display font-semibold tabular-nums ${tint}`}>{value}</div>
    </div>
  );
}

function NumInput({ label, value, onChange, step = 100, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
      <input
        type="number"
        inputMode="decimal"
        value={value}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full bg-background border border-border rounded-md px-3 py-1.5 text-sm tabular-nums focus:border-primary focus:outline-none"
        data-testid={testId}
      />
    </label>
  );
}

function TextInput({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-background border border-border rounded-md px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
        data-testid={testId}
      />
    </label>
  );
}

function SkeletonCard() {
  return (
    <div
      className="rounded-xl border border-border bg-card/40 p-4 h-72 flex items-center justify-center text-muted-foreground text-xs"
      data-testid="projections-loading"
    >
      Running projection…
    </div>
  );
}

function ErrCard({ msg }) {
  return (
    <div
      className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-600 dark:text-red-300 flex items-start gap-3"
      data-testid="projections-error"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold">Could not run projection</div>
        <div className="mt-1 text-xs opacity-90">{msg}</div>
      </div>
    </div>
  );
}

function IncompletePlanNotice() {
  return (
    <div
      className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-5 text-sm"
      data-testid="projections-incomplete"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-display font-semibold">Add an income source to run projections</div>
          <div className="mt-1 text-muted-foreground leading-relaxed">
            The projection engine needs at least one income source (salary, freelance,
            business, etc.) to compute lifetime earnings, taxes, and net worth.
          </div>
          <div className="mt-3">
            <a
              href="#section-income"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-3.5 py-2 rounded-md text-xs font-semibold hover:opacity-90"
              data-testid="projections-add-income-cta"
            >
              Go to income sources
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function LockedTab({ tabId, tier, pitch, featureKey }) {
  const tierName = tier || "Pro";
  return (
    <div
      className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/5 via-transparent to-purple-500/5 p-6"
      data-testid={`projections-locked-${tabId}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
          <Lock className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-primary">
            {tierName} feature
          </div>
          <div className="mt-1 font-display font-semibold text-base">
            Unlock {TABS.find((t) => t.id === tabId)?.label || "this projection"}
          </div>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-2xl">
            {pitch}
          </p>
          <div className="mt-4">
            <Link
              to="/app/pricing"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-semibold hover:opacity-90"
              data-testid={`upsell-${featureKey || tabId}`}
            >
              Unlock with {tierName}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
